import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import api from '../../utils/api';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../../utils/formatters';

const toDate = (value) => {
  if (value == null || value === '') return null;

  let date;
  if (value instanceof Date) {
    date = value;
  } else if (value && typeof value.toDate === 'function') {
    date = value.toDate();
  } else if (value && typeof value === 'object') {
    const seconds = value._seconds ?? value.seconds;
    date = Number.isFinite(Number(seconds)) ? new Date(Number(seconds) * 1000) : new Date(NaN);
  } else if (typeof value === 'string') {
    const normalized = value.trim()
      .replace(/\s+at\s+/i, ' ')
      .replace(/\bUTC([+-])(\d{1,2})(?::?(\d{2}))?\b/i, (_, sign, hours, minutes = '00') => (
        `GMT${sign}${hours.padStart(2, '0')}${minutes.padStart(2, '0')}`
      ));
    date = new Date(normalized);
  } else {
    date = new Date(value);
  }

  return Number.isNaN(date.getTime()) ? null : date;
};

const SoldItems = () => {
  const navigate = useNavigate();
  
  // State management
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: '3months', label: 'Last 3 Months' }
  ];

  // Safe data transformation helper
  const transformCheckoutItem = useCallback((item, transaction = item) => {
    if (!item || typeof item !== 'object') return null;
    
    return {
      id: transaction.id || item.id || `item-${Date.now()}-${Math.random()}`,
      productId: item.productId || '',
      barcode: item.barcode || '',
      name: item.name || item.productName || 'Unnamed Product',
      price: Number(transaction.price ?? transaction.unitPrice ?? item.price) || 0,
      quantity: Number(transaction.quantity ?? item.quantity) || 0,
      category: item.category || 'Uncategorized',
      cost: Number(item.cost) || 0,
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      sku: item.sku || '',
      supplier: item.supplier || 'Unknown Supplier',
      createdAt: [
        transaction.createdAt,
        transaction.updatedAt,
        item.updatedAt,
        item.createdAt
      ].map(toDate).find(Boolean) || null
    };
  }, []);

  // Fetch sold items with comprehensive error handling
  const fetchSoldItems = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/checkouts');
      
      // Safely extract data
      const data = response?.data;
      if (!data) {
        throw new Error('No data received from server');
      }

      // Handle different response formats
      let checkouts = [];
      if (Array.isArray(data)) {
        checkouts = data;
      } else if (data.checkouts && Array.isArray(data.checkouts)) {
        checkouts = data.checkouts;
      } else if (data.error) {
        toast.error(data.error || 'Authentication required');
        setItems([]);
        return;
      } else {
        console.warn('Unexpected response format:', data);
        checkouts = [];
      }

      // Transform and filter valid items
      const soldItems = checkouts
        .filter(item => item && item.productId) // Only item-level docs with productId
        .flatMap(item => {
          const history = Array.isArray(item.history) && item.history.length > 0
            ? item.history
            : [item];
          return history.map((transaction, index) => transformCheckoutItem({
            ...item,
            id: `${item.id || 'item'}-${index}`,
            ...transaction
          }, transaction));
        })
        .filter(Boolean); // Remove any null transformations

      setItems(soldItems);
    } catch (error) {
      console.error('Error fetching sold items:', error);
      
      // Handle specific error types
      if (error.response?.status === 401) {
        toast.error('Please sign in to view sold items');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view sold items');
      } else if (error.code === 'NETWORK_ERROR') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to load sold items. Please try again.');
      }
      
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [transformCheckoutItem]);

  // Safe date comparison helper
  const isDateInRange = useCallback((itemDate, rangeType) => {
    if (!itemDate || rangeType === 'all') return true;
    
    try {
      const date = toDate(itemDate);
      if (!date) return false;
      
      const now = new Date();
      let startDate;

      switch (rangeType) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        case '3months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          break;
        default:
          return true;
      }

      return date >= startDate;
    } catch (error) {
      console.warn('Date comparison error:', error);
      return false;
    }
  }, []);

  // Safe category matching helper
  const matchesCategory = useCallback((itemCategory, filterCategory) => {
    if (!filterCategory) return true;
    if (!itemCategory) return false;
    
    if (Array.isArray(itemCategory)) {
      return itemCategory.includes(filterCategory);
    }
    
    return itemCategory === filterCategory;
  }, []);

  // Apply filters with comprehensive safety checks
  const applyFilters = useCallback(() => {
    try {
      let filtered = [...items];

      // Apply search filter
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filtered = filtered.filter(item => {
          const searchableFields = [
            item.name,
            item.sku,
            item.barcode,
            item.description,
            item.supplier
          ];
          
          return searchableFields.some(field => 
            field && typeof field === 'string' && field.toLowerCase().includes(query)
          );
        });
      }

      // Apply date range filter
      if (dateRange !== 'all') {
        filtered = filtered.filter(item => isDateInRange(item.createdAt, dateRange));
      }

      // Apply category filter
      if (categoryFilter) {
        filtered = filtered.filter(item => matchesCategory(item.category, categoryFilter));
      }

      // Apply sorting with safe comparisons
      filtered.sort((a, b) => {
        const key = sortConfig.key;
        const direction = sortConfig.direction;
        
        let aValue = a[key];
        let bValue = b[key];

        // Handle date sorting
         if (key === 'createdAt') {
           try {
             let aDate = toDate(aValue) || new Date(0);
             let bDate = toDate(bValue) || new Date(0);
             
             if (isNaN(aDate.getTime())) aDate = new Date(0);
             if (isNaN(bDate.getTime())) bDate = new Date(0);
             
             return direction === 'asc' ? aDate - bDate : bDate - aDate;
          } catch (error) {
            console.warn('Date sorting error:', error);
            return 0;
          }
        }

        // Handle string sorting
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        // Handle numeric sorting
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      });

      setFilteredItems(filtered);
    } catch (error) {
      console.error('Error applying filters:', error);
      setFilteredItems([]);
    }
  }, [items, searchQuery, dateRange, categoryFilter, sortConfig, isDateInRange, matchesCategory]);

  // Get unique categories safely
  const categoryOptions = React.useMemo(() => {
    try {
      const categories = new Set();
      
      items.forEach(item => {
        if (Array.isArray(item.category)) {
          item.category.forEach(cat => cat && categories.add(cat));
        } else if (item.category) {
          categories.add(item.category);
        }
      });

      return [
        { value: '', label: 'All Categories' },
        ...Array.from(categories).sort().map(category => ({
          value: category,
          label: category
        }))
      ];
    } catch (error) {
      console.warn('Error building category options:', error);
      return [{ value: '', label: 'All Categories' }];
    }
  }, [items]);

  // Handle sorting with validation
  const handleSort = useCallback((key) => {
    if (!key || typeof key !== 'string') return;
    
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Get sort icon safely
  const getSortIcon = useCallback((key) => {
    try {
      if (sortConfig.key !== key) {
        return <Icon name="ChevronsUpDown" size={16} />;
      }
      
      return sortConfig.direction === 'asc' 
        ? <Icon name="ChevronUp" size={16} />
        : <Icon name="ChevronDown" size={16} />;
    } catch (error) {
      console.warn('Error rendering sort icon:', error);
      return <Icon name="ChevronsUpDown" size={16} />;
    }
  }, [sortConfig]);

  // Safe input handlers
  const handleSearchChange = useCallback((e) => {
    const value = e?.target?.value || '';
    setSearchQuery(value);
  }, []);

  const handleDateRangeChange = useCallback((value) => {
    setDateRange(value || 'all');
  }, []);

  const handleCategoryChange = useCallback((value) => {
    setCategoryFilter(value || '');
  }, []);

  const handleRefresh = useCallback(() => {
    fetchSoldItems();
  }, [fetchSoldItems]);

  const handleExport = useCallback(() => {
    toast.info('Export functionality will be implemented soon');
  }, []);

  // Effects
  useEffect(() => {
    fetchSoldItems();
  }, [fetchSoldItems]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Safe date formatting
  const formatItemDate = useCallback((date) => {
    try {
      const dateObj = toDate(date);
      if (!dateObj) return 'Unknown Date';
      
      return formatDate(dateObj);
    } catch (error) {
      console.warn('Date formatting error:', error);
      return 'Unknown Date';
    }
  }, []);

  // Safe category display
  const formatCategory = useCallback((category) => {
    try {
      if (Array.isArray(category)) {
        return category.filter(Boolean).join(', ') || 'Uncategorized';
      }
      return category || 'Uncategorized';
    } catch (error) {
      console.warn('Category formatting error:', error);
      return 'Uncategorized';
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Sold Items - InventoryTracker</title>
        <meta name="description" content="View and manage items that have been sold or used" />
      </Helmet>
      
      <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
          <Breadcrumb />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Sold/Used Items</h1>
              <p className="text-muted-foreground">
                View and manage items that have been sold or used from inventory
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
                onClick={handleExport}
              >
                Export
              </Button>
              <Button
                variant="default"
                iconName="RefreshCw"
                iconPosition="left"
                iconSize={16}
                onClick={handleRefresh}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
              <Select
                options={dateRangeOptions}
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder="Select date range"
              />
              <Select
                options={categoryOptions}
                value={categoryFilter}
                onChange={handleCategoryChange}
                placeholder="Select category"
              />
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50">
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer" 
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Product</span>
                        <span className="inline-block w-4 h-4">{getSortIcon('name')}</span>
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer" 
                      onClick={() => handleSort('sku')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>SKU/Barcode</span>
                        <span className="inline-block w-4 h-4">{getSortIcon('sku')}</span>
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer" 
                      onClick={() => handleSort('quantity')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Quantity</span>
                        <span className="inline-block w-4 h-4">{getSortIcon('quantity')}</span>
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer" 
                      onClick={() => handleSort('price')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Price</span>
                        <span className="inline-block w-4 h-4">{getSortIcon('price')}</span>
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer" 
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date Sold</span>
                        <span className="inline-block w-4 h-4">{getSortIcon('createdAt')}</span>
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer" 
                      onClick={() => handleSort('category')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Category</span>
                        <span className="inline-block w-4 h-4">{getSortIcon('category')}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-10 text-center text-muted-foreground">
                        <div className="flex items-center justify-center space-x-2">
                          <Icon name="Loader2" size={20} className="animate-spin" />
                          <span>Loading sold items...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-10 text-center text-muted-foreground">
                        <div className="flex flex-col items-center space-y-2">
                          <Icon name="Package" size={48} className="text-muted-foreground/50" />
                          <span>No sold items found</span>
                          <span className="text-sm">Try adjusting your filters or refresh the page</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item, index) => (
                      <tr key={`${item.id}-${index}`} className="hover:bg-muted/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {item.imageUrl ? (
                              <img 
                                src={item.imageUrl} 
                                alt={item.name || 'Product'} 
                                className="w-10 h-10 rounded-md object-cover mr-3"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://via.placeholder.com/40x40?text=No+Image';
                                }}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center mr-3">
                                <Icon name="Image" size={16} className="text-muted-foreground" />
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-foreground">
                                {item.name || 'Unnamed Product'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {item.supplier || 'Unknown Supplier'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {item.sku || item.barcode || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {item.quantity || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {formatCurrency(item.price || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatItemDate(item.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatCategory(item.category)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          {!isLoading && filteredItems.length > 0 && (
            <div className="mt-6 text-sm text-muted-foreground text-center">
              Showing {filteredItems.length} of {items.length} sold items
            </div>
          )}
        </div>
      </div>
  );
};

export default SoldItems;